import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import WhatsAppButton from "@/components/WhatsAppButton";

import SkipToContent from "@/components/SkipToContent";
import HomeProcessSection from "@/components/HomeProcessSection";
import heroImage from "@/assets/hero-home.jpg";
const HomePage = () => {
  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>אמון בזוגיות | שירות שידוכים פרימיום</title>
        <meta
          name="description"
          content="אמון בזוגיות - שירות שידוכים פרימיום ואישי. מתמחים במציאת זוגיות איכותית למבוגרים, אנשי קריירה ומחפשי זוגיות רצינית. שידוכים מקצועיים עם ליווי אישי."
        />
        <meta
          name="keywords"
          content="שידוכים, שירות שידוכים, שדכן, זוגיות, הכרויות, שידוכים למבוגרים"
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://amon-match.co.il/" />

        {/* Open Graph */}
        <meta property="og:title" content="אמון בזוגיות | שירות שידוכים פרימיום" />
        <meta
          property="og:description"
          content="שירות שידוכים אישי ומקצועי למבוגרים ואנשי קריירה"
        />
        <meta property="og:image" content="https://amon-match.co.il/og-image.png" />
        <meta property="og:url" content="https://amon-match.co.il/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="he_IL" />

        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="אמון בזוגיות | שירות שידוכים פרימיום" />
        <meta name="twitter:description" content="שירות שידוכים אישי ומקצועי" />
        <meta name="twitter:image" content="https://amon-match.co.il/og-image.png" />

        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "אמון בזוגיות",
            "description": "שירות שידוכים פרימיום",
            "url": "https://amon-match.co.il",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "IL"
            },
            "areaServed": "Israel",
            "serviceType": "Matchmaking Service"
          })}
        </script>
      </Helmet>

      <SkipToContent />

      <div className="relative min-h-screen flex flex-col">
        {/* Background Image with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
          role="img"
          aria-hidden="true"
        >
          <div className="absolute inset-0 bg-gradient-hero" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col min-h-screen">
          {/* Header */}
          <header role="banner" className="container mx-auto px-6 py-8 flex justify-center">
            <Logo variant="light" />
          </header>

          {/* Hero Content */}
          <main id="main-content" role="main" tabIndex={-1} className="flex-1 flex items-center justify-center px-6">
            <div className="text-center max-w-4xl mx-auto">
              {/* Headline */}
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-cream mb-6 leading-tight animate-fade-up opacity-0">
                מפסיקים להמר על הזמן שלך.
                <br />
                מחברים אותך לזוגיות מדויקת.
              </h1>

              {/* Sub-headline */}
              <p className="text-lg md:text-xl text-cream/90 mb-8 leading-relaxed max-w-3xl mx-auto animate-fade-up opacity-0 delay-200">
                בזמן שהאפליקציות מוכרות כמות ורעש, אנחנו מתמקדים באיכות. המאגר
                היחיד שבו כל התאמה עוברת סינון כפול: אלגוריתם ומאמן אישי.
              </p>

              {/* Process Section */}
              <HomeProcessSection />

              {/* Split Buttons */}
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center animate-fade-up opacity-0 delay-400">
                <Link to="/men">
                  <Button variant="hero-primary" size="xl" className="min-w-[280px]">
                    אני גבר - לבדיקת התאמה
                  </Button>
                </Link>
                <Link to="/women">
                  <Button variant="hero-secondary" size="xl" className="min-w-[280px]">
                    אני אישה - לבדיקת התאמה
                  </Button>
                </Link>
              </div>

              {/* Microcopy */}
              <p className="text-cream/70 text-sm mt-4 animate-fade-up opacity-0 delay-500">
                ללא התחייבות • שיחת היכרות חינם
              </p>

              {/* EVA Protocol CTA */}
              <section
                aria-labelledby="eva-protocol-heading"
                className="mt-16 pt-8 border-t border-cream/20 animate-fade-up opacity-0"
                style={{
                  animationDelay: "600ms",
                  animationFillMode: "forwards",
                }}
              >
                <h2 id="eva-protocol-heading" className="text-2xl md:text-3xl font-bold text-cream mb-4">
                  אם אפליקציות היו עובדות, לא היית כאן
                </h2>
                <p className="text-cream/80 mb-6 max-w-2xl mx-auto text-base md:text-lg">
                  הכירו את פרוטוקול EVA: השיטה שמפרקת זוגיות לגורמים ומבטיחה
                  יציבות לפני המשיכה.
                </p>
                <Link to="/eva-protocol">
                  <Button
                   variant="default"
                   className="bg-gold text-navy hover:bg-gold/90 font-semibold px-8"
                  >
                    עוד על השיטה
                  </Button>
                </Link>
              </section>
            </div>
          </main>

          {/* Footer */}
          <footer role="contentinfo" className="container mx-auto px-6 py-8 text-center">
            <nav aria-label="קישורי פוטר" className="mb-4">
              <ul className="flex justify-center gap-6 text-sm text-cream/60">
                <li>
                  <Link to="/privacy" className="hover:text-gold transition-colors">
                    מדיניות פרטיות
                  </Link>
                </li>
                <li>
                  <Link to="/accessibility" className="hover:text-gold transition-colors">
                    הצהרת נגישות
                  </Link>
                </li>
              </ul>
            </nav>
            <p className="text-cream/50 text-sm">
              © {new Date().getFullYear()} אמון בזוגיות. כל הזכויות שמורות.
            </p>
          </footer>
        </div>

        <WhatsAppButton />
      </div>
    </>
  );
};

export default HomePage;
